import React from 'react'
import { observer } from 'mobx-react-lite'
import { subDays } from 'date-fns/subDays'
import { startOfToday } from 'date-fns/startOfToday'
import { endOfMonth } from 'date-fns/endOfMonth'

import Button from '@mui/material/Button'
import Divider from '@mui/material/Divider'
import List from '@mui/material/List'
import Stack from '@mui/material/Stack'

import { Delete } from '@mxenabled/mx-icons'

import { MerchantLogo, P, Text } from '@mxenabled/mxui'

import {
  ANALYTICS_EVENTS,
  DataRow,
  Dialog,
  Drawer,
  useAccountStore,
  useEvent,
  useGlobalCopyStore,
  useGlobalUiStore,
  useRecurringTransactionsStore,
  useTransactionStore,
} from '../../common'
import {
  type Member,
  type Recurrence,
  type RepeatingTransaction,
  type Transaction,
  RecurrenceStatus,
  RepeatingType,
} from '../../core'

import { TransactionDetails } from '../../transactions'
import { AccountDetailsDrawer, useAccountUiStore } from '../../accounts'

import { formatCurrency } from '../../core/utils/NumberFormatting'
import { getNextUpcomingDate } from '../../core/utils/RecurringTransactionsUtil'
import { LinkTransactionDrawer } from './actions/LinkTransactionDrawer'
import { MarkAsPaidDialog } from './actions/MarkAsPaidDialog'
import { MarkAsUnpaidDialog } from './actions/MarkAsUnpaidDialog'
import { PaymentScheduleAction } from './actions/PaymentScheduleAction'
import { RecurringTransactionList } from './RecurringTransactionList'
import { UpcomingRecurrenceCard } from './UpcomingRecurrenceCard'

interface RecurringTransactionDetailsProps {
  onDeleted: () => void
  recurringTransaction: RepeatingTransaction
  selectedRecurrence: Recurrence | null
}

export const RecurringTransactionDetails = observer(
  ({ onDeleted, recurringTransaction, selectedRecurrence }: RecurringTransactionDetailsProps) => {
    const { recurring: copy } = useGlobalCopyStore()
    const { selectedAccountGuids } = useGlobalUiStore()
    const { onEvent } = useEvent()
    const { deleteRepeatingTransaction, markRecurrenceAsUnpaid, recurrences } =
      useRecurringTransactionsStore()
    const { detailedTransactions, setFilter, transactions } = useTransactionStore()
    const { setSelectedAccount, setSelectedMember } = useAccountUiStore()
    const { members } = useAccountStore()
    const { amount, description, merchant_guid } = recurringTransaction

    const [showAccountDetails, setShowAccountDetails] = React.useState(false)
    const [showDeleteConfirm, setShowDeleteConfirm] = React.useState(false)
    const [showMarkAsPaid, setShowMarkAsPaid] = React.useState(false)
    const [showMarkAsUnpaid, setShowMarkAsUnpaid] = React.useState(false)
    const [unlinkTransactionGuid, setUnlinkTransactionGuid] = React.useState('')
    const [showLinkTransaction, setShowLinkTransaction] = React.useState(false)
    const [transactionGuid, setTransactionGuid] = React.useState('')

    React.useEffect(() => {
      setFilter({
        accounts: selectedAccountGuids,
        dateRange: { start: subDays(startOfToday(), 90), end: endOfMonth(startOfToday()) },
        custom: (t: Transaction) => t.repeating_transaction_guid === recurringTransaction.guid,
      })
    }, [recurringTransaction.guid])

    const transaction = React.useMemo(
      () => detailedTransactions.find((t) => t.guid === transactionGuid),
      [transactionGuid, detailedTransactions],
    )

    const isIncome = recurringTransaction.repeating_transaction_type === RepeatingType.Income

    const filteredTransactions = React.useMemo(
      () => transactions.filter((t) => t.repeating_transaction_guid === recurringTransaction.guid),
      [transactions, recurringTransaction.guid],
    )

    const nextUpcomingDate = getNextUpcomingDate(recurringTransaction, recurrences)

    const handleAccountClick = () => {
      if (recurringTransaction.account) {
        setSelectedAccount(recurringTransaction.account)
        const member =
          members.find((m: Member) => m.guid === recurringTransaction.account?.member_guid) ||
          ({} as Member)
        if (member) {
          setSelectedMember(member)
        }
        setShowAccountDetails(true)
      }
    }

    const handleDeleteRecurringTransaction = async () => {
      await deleteRepeatingTransaction(recurringTransaction.guid)
      onDeleted()
    }

    const handleDeleteButtonClick = () => {
      setShowDeleteConfirm(true)

      onEvent(ANALYTICS_EVENTS.RECURRING_TRANSACTIONS_DETAILS_CLICK_DELETE)
    }

    const handleMarkAsPaidClick = () => {
      setShowMarkAsPaid(true)
      onEvent(ANALYTICS_EVENTS.RECURRING_TRANSACTIONS_LIST_CLICK_MARK_AS_PAID)
    }

    const handleLinkTransaction = () => {
      setShowMarkAsPaid(false)
      setShowLinkTransaction(true)
    }

    const handleMarkAsUnpaidClick = (transactionGuid: string) => {
      setUnlinkTransactionGuid(transactionGuid)
      setShowMarkAsUnpaid(true)
      onEvent(ANALYTICS_EVENTS.RECURRING_TRANSACTIONS_LIST_CLICK_MARK_AS_UNPAID)
    }

    const handleUnlinkTransaction = async () => {
      const txn = transactions.find((t) => t.guid === unlinkTransactionGuid)
      if (txn) await markRecurrenceAsUnpaid(txn)

      setShowMarkAsUnpaid(false)
      setUnlinkTransactionGuid('')
    }

    return (
      <Stack bgcolor="background.default" direction="column" height="100%">
        <Stack direction="column" m={24}>
          <MerchantLogo
            categoryGuid={transaction?.category_guid || ''}
            merchantGuid={merchant_guid || ''}
            size={64}
            sx={{ mb: 12 }}
          />
          <Text bold={true} variant="body1">
            {description}
          </Text>
          <P color={isIncome ? 'success.main' : 'text.primary'} sx={{ mt: 4, mb: 16 }} variant="h1">
            {isIncome ? '+' : ''}
            {formatCurrency(amount, '0,0.00')}
          </P>
          <UpcomingRecurrenceCard
            expectedDate={
              selectedRecurrence?.occurredOnDate ||
              selectedRecurrence?.expectedDate ||
              nextUpcomingDate
            }
            onMarkAsPaidClick={
              selectedRecurrence?.status === RecurrenceStatus.Paid &&
              selectedRecurrence.transaction?.guid
                ? () => handleMarkAsUnpaidClick(selectedRecurrence.transaction!.guid)
                : handleMarkAsPaidClick
            }
            status={selectedRecurrence?.status}
          />
        </Stack>
        <List sx={{ bgcolor: 'background.paper' }}>
          {recurringTransaction.account && (
            <>
              <DataRow
                iconName="checkbook"
                label={copy.account}
                onClick={handleAccountClick}
                showChevron={false}
                value={recurringTransaction.account.displayName || ''}
              />
              <Divider variant="inset" />
            </>
          )}

          {transaction?.category && (
            <>
              <DataRow
                iconName="monetization_on"
                label={copy.category}
                value={transaction.category}
              />
              <Divider variant="inset" />
            </>
          )}

          <PaymentScheduleAction repeatingTransaction={recurringTransaction} />
          <Divider />
        </List>

        <Button
          color="error"
          onClick={handleDeleteButtonClick}
          startIcon={<Delete />}
          sx={{ mx: 24, my: 16 }}
          variant="text"
        >
          {isIncome ? copy.delete_income_title : copy.delete_expense_title}
        </Button>

        <RecurringTransactionList
          isIncome={isIncome}
          onMarkAsPaidClick={handleMarkAsPaidClick}
          onMarkAsUnpaidClick={handleMarkAsUnpaidClick}
          onTransactionClick={setTransactionGuid}
          recurringTransaction={recurringTransaction}
          transactions={filteredTransactions}
        />

        <Drawer
          ariaLabelClose={copy.close_drawer_aria}
          isOpen={!!transactionGuid && Boolean(transaction)}
          onClose={() => setTransactionGuid('')}
          title={copy.transaction_details_drawer_title}
        >
          {transaction && <TransactionDetails transaction={transaction} />}
        </Drawer>

        <Dialog
          copy={{
            title: isIncome ? copy.delete_income_title : copy.delete_expense_title,
            close_aria: copy.close_drawer_aria,
          }}
          isOpen={showDeleteConfirm}
          onClose={() => setShowDeleteConfirm(false)}
          onPrimaryAction={handleDeleteRecurringTransaction}
        >
          <Text>{isIncome ? copy.delete_income_description : copy.delete_expense_description}</Text>
        </Dialog>

        <MarkAsPaidDialog
          isOpen={showMarkAsPaid}
          onClose={() => setShowMarkAsPaid(false)}
          onLinkTransaction={handleLinkTransaction}
        />

        <MarkAsUnpaidDialog
          isOpen={showMarkAsUnpaid}
          onClose={() => {
            setShowMarkAsUnpaid(false)
            setUnlinkTransactionGuid('')
          }}
          onUnlinkTransaction={handleUnlinkTransaction}
        />

        <LinkTransactionDrawer
          isOpen={showLinkTransaction}
          onClose={() => setShowLinkTransaction(false)}
          recurringTransaction={recurringTransaction}
        />

        <AccountDetailsDrawer
          isOpen={showAccountDetails}
          onClose={() => setShowAccountDetails(false)}
        />
      </Stack>
    )
  },
)
